import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { LocalCartService } from 'src/app/nio-common/services/local-cart.service';
import { GoogleAnalyticsEvents } from '../../models/google-analytics-events.enum';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';
import { PhotoService } from '../../services/photo.service';

interface ModalPhotoViewerContent {
  imageUrl: string;
  price?: number;
  fallbackPhotoId: string;
  downloadable?: boolean;
  isAdminView?: boolean; // New field to determine admin vs user context
  eventName?: string; // Event name for cart display
  eventId?: string; // Event ID for cart storage
}

@Component({
  selector: 'app-modal-photo-viewer',
  templateUrl: './modal-photo-viewer.component.html',
  styleUrls: ['./modal-photo-viewer.component.scss'],
})
export class ModalPhotoViewerComponent {
  content!: ModalPhotoViewerContent;

  constructor(
    private router: Router,
    private photoService: PhotoService,
    private localCartService: LocalCartService,
    private modal: NgbActiveModal,
    private toastrService: ToastrService,
    private googleAnalyticsService: GoogleAnalyticsService,
  ) {}

  close(confirmed: boolean): void {
    this.modal.close(confirmed);
  }

  get alreadyInCart(): boolean {
    return this.localCartService.isInCart(this.content.fallbackPhotoId);
  }

  async onImageError() {
    const photo = await this.photoService.getPhotoById(this.content.fallbackPhotoId);
    if (photo) {
      this.content.imageUrl = photo.url;
    }
  }

  async downloadPhoto() {
    try {
      await this.photoService.downloadPhotoByIds(this.content.fallbackPhotoId);

      // Track successful download
      this.googleAnalyticsService.trackEventWithDimensions(
        GoogleAnalyticsEvents.FREE_PHOTO_DOWNLOADED,
        {
          photo_id: this.content.fallbackPhotoId,
          event_id: this.content.eventId,
          event_name: this.content.eventName,
          download_type: 'free_download',
        },
      );
    } catch (error) {
      this.toastrService.error('Failed to download photo', 'Error');
    }
  }

  async onAddToCart() {
    try {
      // For guest users (non-admin view), use localStorage cart
      if (!this.content.isAdminView && this.content.price !== undefined) {
        const cartItem = {
          photoId: this.content.fallbackPhotoId,
          eventId: this.content.eventId || '',
          eventName: this.content.eventName || 'Unknown Event',
          price: this.content.price,
        };

        this.localCartService.addToCart(cartItem);
        this.toastrService.success('Photo added to cart', 'Success');
        this.modal.close(true);
        return;
      }

      // For admin users or legacy cases, handle differently
      this.toastrService.error('Add to cart is not available in admin view', 'Error');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (ex: any) {
      this.toastrService.error('Failed to add to cart', 'Error');
    }
  }

  async viewCart() {
    this.modal.close(true);
    await this.router.navigate(['/user/cart']);
  }

  async copyPhotoId() {
    try {
      await navigator.clipboard.writeText(this.content.fallbackPhotoId);
      this.toastrService.success('Photo ID copied to clipboard', 'Success');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = this.content.fallbackPhotoId;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        this.toastrService.success('Photo ID copied to clipboard', 'Success');
      } catch (fallbackError) {
        this.toastrService.error('Failed to copy Photo ID', 'Error');
      }
      document.body.removeChild(textArea);
    }
  }

  orderProfessionalPrint() {
    const phoneNumber = '+60173173781';
    const photoId = this.content.fallbackPhotoId;
    const message = `Hi! I would like to order a print with frame for Photo ID: ${photoId}. Please provide details on information. Thank you!`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);

    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${phoneNumber.replace('+', '')}?text=${encodedMessage}`;

    // Open in new tab/window
    window.open(whatsappUrl, '_blank');

    // Show success message
    this.toastrService.info('Opening WhatsApp to place your order...', 'Print');
  }
}
